/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  ScatterChart,
  LabelList,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import {
  StrategyCompassXYZAxis,
  StrategyCompassXYZAxisReadableMap,
} from '../../../types';
import memoize from 'lodash/memoize';
import range from 'lodash/range';
import { useEffect, useRef, useState } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { ColorMode } from 'theme';
import { useRecoilValue } from 'recoil';
import { equitiesFetchLoadingState } from '../../../states';
import { difference } from 'lodash';
import { Box } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import {
  CompassChartData,
  CompassZoomDomain,
  StrategyCompassMode,
} from '../../../types/compass';
import { generateAxisLabelTooltip, isValidCompassSymbol } from 'util/compass';
import { pairWise } from 'util/shared/common';
import { StrategyCompassControls } from './StrategyCompassControls';
import { StrategyCompassTooltip } from './StrategyCompassTooltip';
import { Loader } from '../../shared';
import { MAX_COMPASS_SYMS } from '../../../hooks/scanners/useCompassData';
import { SGTooltip } from 'components/core';
import { useCompassCtx } from './useCompassCtx';
import { COMPASS_DEFAULT_ZOOM_DOMAIN_WITH_BUFFER } from 'config/compass';

const EDGE_ALLOWED_SPACING = 30;
// Define a buffer zone to prevent tooltip flickering at the edges
const TOOLTIP_EDGE_BUFFER = 10;

const X_MIN = 0;
const X_MAX = 1;
const SEGMENT_PADDING = X_MAX * 0.02;
const Y_MIN = 0;
const Y_MAX = 1;
const X_MID = (X_MIN + X_MAX) / 2.0;
const Y_MID = (Y_MIN + Y_MAX) / 2.0;

type StrategyCompassProps = {
  chartFullscreen: boolean;
  setChartFullscreen: (fullscreen: boolean) => void;
};

// creates an interpolated colors with  manipulated opacity, saturation, and
// lightness for a point in the history line from [0,1]
const historyColorInterpolation = memoize(
  (
    historyPosition: number,
    hue: number,
    saturation: number,
    lightness: number,
  ): { opacity: number; hsl: string; hsla: string } => {
    const opacity = Math.min(
      1,
      Math.max(0, 0.85 * Math.pow(historyPosition, 3) + 0.15),
    );
    const newSaturation = Math.round(
      Math.min(1, Math.max(0, 0.5 * Math.pow(historyPosition, 1) + 0.5)) *
        saturation,
    );
    const newLightness =
      lightness ||
      Math.round(
        Math.min(1, Math.max(0, 0.8 * Math.pow(historyPosition, 1) + 0.2)) *
          lightness,
      );
    const hsl = `hsl(${hue},${newSaturation}%,${newLightness}%)`;
    const hsla = `hsla(${hue},${newSaturation}%,${newLightness}%,${opacity})`;
    return {
      opacity,
      hsl,
      hsla,
    };
  },
  // cache hash key on all parameters
  (...args) => args.join(','),
);

export const StrategyCompass = ({
  chartFullscreen,
  setChartFullscreen,
}: StrategyCompassProps) => {
  const ctx = useCompassCtx();
  const { compassParams, compassData } = ctx;
  const { mode, syms, xAxis, yAxis, editable, zAxis } = compassParams;
  const {
    fetchData,
    loading,
    chartData,
    data,
    visibleChartData,
    compassHistoryFromSym,
  } = compassData;

  const theme = useTheme();
  const axisTextColor = theme.palette.text.secondary;
  const { posColor, negColor } = ctx;
  const equitiesLoading = useRecoilValue(equitiesFetchLoadingState);
  const { hoveredSymbol } = ctx;
  const isMobile = useIsMobile();
  const compassHistoryLookback = ctx.config.historyLookback;
  const hoveredSymData = ctx.hoveredSymData;
  const setHoveredSymData = ctx.setHoveredSymData;

  const fontSize = isMobile ? 11 : 13;
  const [chartSize, setChartSize] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 },
  );

  // Zoom state from context
  const { zoomDomain, setZoomDomain, isZoomed, setIsZoomed } = ctx;
  const chartRef = useRef<any>(null);

  // Box selection zoom state from context
  const { zoomStartPoint, setZoomStartPoint } = ctx;
  const { zoomEndPoint, setZoomEndPoint } = ctx;
  const { isSelecting, setIsSelecting } = ctx;

  // State to track if tooltip should be shown (to prevent flickering)
  const [tooltipStable, setTooltipStable] = useState(true);
  // Ref to track mouse position
  const mousePositionRef = useRef({ x: 0, y: 0 });
  // State to track if mouse is over a data point
  const [activeTooltip, setActiveTooltip] = useState(false);

  const shadeOverlayColor = alpha(theme.palette.text.primary, 0.1); // light shade color

  const isProximityAxis = (axis: StrategyCompassXYZAxis) => {
    return (
      axis === StrategyCompassXYZAxis.ProximityToCallWall ||
      axis === StrategyCompassXYZAxis.ProximityToPutWall
    );
  };

  useEffect(() => {
    // it's possible we've fetched data for these symbols already and they are just being re-enabled
    // if so, dont fetch again
    const currSymsFetched = [...data.keys()];
    const newSymsToFetch = difference(syms, currSymsFetched).filter(
      isValidCompassSymbol,
    );
    if (newSymsToFetch.length === 0) {
      return;
    }

    void fetchData(newSymsToFetch);
  }, [fetchData, syms, data]);

  const yAxisTitle = StrategyCompassXYZAxisReadableMap.get(yAxis)!;
  const xAxisTitle = StrategyCompassXYZAxisReadableMap.get(xAxis)!;
  const zAxisTitle =
    zAxis == null || zAxis === StrategyCompassXYZAxis.None
      ? null
      : StrategyCompassXYZAxisReadableMap.get(zAxis)!;

  const lineColor = mode === StrategyCompassMode.Compass ? '#000' : '#fff';
  const strokeWidth = mode === StrategyCompassMode.Compass ? 1.5 : 0.5;
  const axisLinesComponent = (
    <>
      <ReferenceLine
        segment={[
          { x: X_MIN - SEGMENT_PADDING, y: Y_MID },
          { x: X_MAX + SEGMENT_PADDING, y: Y_MID },
        ]}
        stroke={lineColor}
        strokeWidth={strokeWidth}
        ifOverflow="hidden"
        label={(_props) => (
          <>
            <SGTooltip title={generateAxisLabelTooltip('high', yAxis)}>
              <text
                x={chartSize.width / 2.0}
                y={15}
                fill={axisTextColor}
                fontSize={12}
                textAnchor="middle"
              >
                {renderLabelText(
                  `${isProximityAxis(yAxis) ? 'Low' : 'High'} ${yAxisTitle}`,
                )}
              </text>
            </SGTooltip>
            <SGTooltip title={generateAxisLabelTooltip('low', yAxis)}>
              <text
                x={chartSize.width / 2.0}
                y={isZoomed ? chartSize.height - 5 : chartSize.height - 10}
                fill={axisTextColor}
                fontSize={12}
                textAnchor="middle"
              >
                {renderLabelText(
                  `${isProximityAxis(yAxis) ? 'High' : 'Low'} ${yAxisTitle}`,
                )}
              </text>
            </SGTooltip>
          </>
        )}
      />
      <ReferenceLine
        segment={[
          { x: X_MID, y: Y_MIN - SEGMENT_PADDING },
          { x: X_MID, y: Y_MAX + SEGMENT_PADDING },
        ]}
        stroke={lineColor}
        strokeWidth={strokeWidth}
        ifOverflow="hidden"
        label={(_props) => (
          <>
            <SGTooltip title={generateAxisLabelTooltip('high', xAxis)}>
              <text
                x={chartSize.width - EDGE_ALLOWED_SPACING}
                y={chartSize.height / 2.0 + (isZoomed ? 0 : 15)}
                fill={axisTextColor}
                fontSize={12}
                textAnchor="middle"
                transform={`rotate(90, ${
                  chartSize.width - EDGE_ALLOWED_SPACING
                }, ${chartSize.height / 2.0})`}
              >
                {renderLabelText(
                  `${isProximityAxis(xAxis) ? 'Low' : 'High'} ${xAxisTitle}`,
                )}
              </text>
            </SGTooltip>

            <SGTooltip title={generateAxisLabelTooltip('low', xAxis)}>
              <text
                x={EDGE_ALLOWED_SPACING}
                y={chartSize.height / 2.0 + (isZoomed ? 0 : 15)}
                fill={axisTextColor}
                fontSize={12}
                textAnchor="middle"
                transform={`rotate(-90, ${EDGE_ALLOWED_SPACING}, ${
                  chartSize.height / 2.0
                })`}
              >
                {renderLabelText(
                  `${isProximityAxis(xAxis) ? 'High' : 'Low'} ${xAxisTitle}`,
                )}
              </text>
            </SGTooltip>
          </>
        )}
      />
    </>
  );

  // Check if a quadrant is visible within the current zoom domain
  const isQuadrantVisible = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    if (!isZoomed) {
      return true;
    }

    // Check if any part of the quadrant is within the zoom domain
    return !(
      (
        x2 < zoomDomain.x[0] || // Quadrant is completely to the left of zoom
        x1 > zoomDomain.x[1] || // Quadrant is completely to the right of zoom
        y2 < zoomDomain.y[0] || // Quadrant is completely below zoom
        y1 > zoomDomain.y[1]
      ) // Quadrant is completely above zoom
    );
  };

  // Calculate the visible portion of a quadrant
  const getVisibleQuadrantCoords = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => {
    if (!isZoomed) {
      return { x1, y1, x2, y2 };
    }

    return {
      x1: Math.max(x1, zoomDomain.x[0]),
      y1: Math.max(y1, zoomDomain.y[0]),
      x2: Math.min(x2, zoomDomain.x[1]),
      y2: Math.min(y2, zoomDomain.y[1]),
    };
  };

  // Calculate label position based on visible quadrant area
  const getQuadrantLabelPosition = (_quadrant: string, visibleCoords: any) => {
    const { x1, y1, x2, y2 } = visibleCoords;
    const centerX = (x1 + x2) / 2;
    const centerY = (y1 + y2) / 2;

    // Map the logical coordinates to screen coordinates
    const screenX =
      (chartSize.width * (centerX - zoomDomain.x[0])) /
      (zoomDomain.x[1] - zoomDomain.x[0]);
    const screenY =
      chartSize.height *
      (1 - (centerY - zoomDomain.y[0]) / (zoomDomain.y[1] - zoomDomain.y[0]));

    return { x: screenX, y: screenY };
  };

  const renderQuadrant = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    labelLines: string[],
  ) => {
    if (!isQuadrantVisible(x1, y1, x2, y2)) {
      return null;
    }

    const visibleCoords = getVisibleQuadrantCoords(x1, y1, x2, y2);
    const position = getQuadrantLabelPosition('', visibleCoords);

    return (
      <ReferenceArea
        x1={visibleCoords.x1}
        y1={visibleCoords.y1}
        x2={visibleCoords.x2}
        y2={visibleCoords.y2}
        fill={shadeOverlayColor}
        ifOverflow="hidden"
        label={(_props) => (
          <text
            x={position.x}
            y={position.y}
            fill={theme.colorMode === ColorMode.LIGHT ? '#4d4d4d' : '#a4a4a4'}
            fontSize={fontSize}
            textAnchor="middle"
            opacity={0.8}
          >
            {createMultilineText(position.x, labelLines)}
          </text>
        )}
      />
    );
  };

  const labeledReferenceAreas = mode === StrategyCompassMode.Compass && (
    <>
      {renderQuadrant(X_MIN, Y_MIN, X_MID, Y_MID, [
        'Cheap Volatility',
        'Upside Potential',
      ])}

      {renderQuadrant(X_MIN, Y_MID, X_MID, Y_MAX, [
        'Expensive Volatility',
        'Upside Potential',
      ])}

      {renderQuadrant(X_MID, Y_MID, X_MAX, Y_MAX, [
        'Expensive Volatility',
        'Downside Potential',
      ])}

      {renderQuadrant(X_MID, Y_MIN, X_MAX, Y_MID, [
        'Cheap Volatility',
        'Downside Potential',
      ])}
    </>
  );

  function renderLabelText(str: string) {
    return chartSize.width > 0 && chartSize.height > 0 ? str : '';
  }

  const createMultilineText = (x: number, lines: string[], lineHeight = 20) => {
    return lines.map((line, i) => (
      <tspan key={i} x={x} dy={i === 0 ? 0 : lineHeight}>
        {line}
      </tspan>
    ));
  };

  const handleZoom = (domain: CompassZoomDomain) => {
    setZoomDomain(domain);
    setIsZoomed(true);
  };

  const handleMouseDown = (e: any) => {
    if (e && e.xValue && e.yValue) {
      setZoomStartPoint({ x: e.xValue, y: e.yValue });
      setIsSelecting(true);
    }
  };

  const handleMouseMove = (e: any) => {
    if (isSelecting && e && e.xValue && e.yValue) {
      setZoomEndPoint({ x: e.xValue, y: e.yValue });
    }
    if (e?.isTooltipActive && !hoveredSymData) {
      setHoveredSymData(e.activePayload?.[0]?.payload);
    }
    // Track mouse position for tooltip stability
    if (e && e.chartX !== undefined && e.chartY !== undefined) {
      mousePositionRef.current = { x: e.chartX, y: e.chartY };

      // Check if mouse is near the edge of the chart
      const isNearEdge =
        e.chartX < TOOLTIP_EDGE_BUFFER ||
        e.chartX > chartSize.width - TOOLTIP_EDGE_BUFFER ||
        e.chartY < TOOLTIP_EDGE_BUFFER ||
        e.chartY > chartSize.height - TOOLTIP_EDGE_BUFFER;

      // Only update tooltip stability if the state needs to change
      if (isNearEdge !== !tooltipStable) {
        setTooltipStable(!isNearEdge);
      }
    }
  };

  const handleMouseUp = (e: any) => {
    if (isSelecting && zoomStartPoint && e && e.xValue && e.yValue) {
      const endPoint = { x: e.xValue, y: e.yValue };

      // Only zoom if the selection box is large enough
      const minSelectionSize = 0.01;
      const xDiff = Math.abs(zoomStartPoint.x - endPoint.x);
      const yDiff = Math.abs(zoomStartPoint.y - endPoint.y);

      if (xDiff > minSelectionSize || yDiff > minSelectionSize) {
        handleZoom({
          x: [
            Math.min(zoomStartPoint.x, endPoint.x),
            Math.max(zoomStartPoint.x, endPoint.x),
          ],
          y: [
            Math.min(zoomStartPoint.y, endPoint.y),
            Math.max(zoomStartPoint.y, endPoint.y),
          ],
        });
      }

      setIsSelecting(false);
      setZoomStartPoint(null);
      setZoomEndPoint(null);
    }
  };

  // Render selection box if user is currently selecting an area
  const renderSelectionBox = () => {
    if (isSelecting && zoomStartPoint && zoomEndPoint) {
      return (
        <ReferenceArea
          x1={zoomStartPoint.x}
          y1={zoomStartPoint.y}
          x2={zoomEndPoint.x}
          y2={zoomEndPoint.y}
          fill={theme.palette.primary.main}
          fillOpacity={0.3}
          stroke={theme.palette.primary.main}
          strokeWidth={1}
          strokeOpacity={0.8}
        />
      );
    }
    return null;
  };

  const visibleChartDataWithPoints = visibleChartData.map((symData, symIdx) => {
    const points =
      compassHistoryLookback === 0
        ? [symData, symData]
        : [symData, ...(compassHistoryFromSym?.[symData.sym] ?? [])];
    const colorIdx = symIdx % theme.palette.compass.historyPalette.length;
    const colorHSL = theme.palette.compass.historyPalette[colorIdx];
    const color =
      compassHistoryLookback === 0
        ? theme.palette.compass.pointColor
        : `hsl(${colorHSL[0]},${colorHSL[1]}%,${colorHSL[2]}%)`;
    return {
      symData,
      symIdx,
      points,
      colorIdx,
      colorHSL,
      color,
    };
  });

  return (
    <Box
      sx={{
        width: '100%',
        height: chartFullscreen ? '100%' : '600px',
        padding: '12px',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: 'calc(100% - 75px)',
          position: 'relative',
        }}
      >
        <StrategyCompassControls
          editable={editable}
          loading={loading}
          chartSize={chartSize}
          chartFullscreen={chartFullscreen}
          setChartFullscreen={setChartFullscreen}
        />
        <ResponsiveContainer
          onResize={(width, height) => setChartSize({ width, height })}
        >
          {equitiesLoading ? (
            <Loader isLoading={true} /> // chart doesnt render correctly as a child of Loader
          ) : (
            <ScatterChart
              ref={chartRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => setActiveTooltip(false)}
              margin={
                isZoomed
                  ? { top: 25, right: 45, bottom: 25, left: 45 }
                  : { top: 15, right: 35, bottom: 15, left: 35 }
              }
            >
              <XAxis
                type="number"
                dataKey="x"
                name={xAxisTitle}
                hide
                domain={[zoomDomain.x[0] - 0.02, zoomDomain.x[1] + 0.02]}
                allowDataOverflow
              />
              <YAxis
                type="number"
                hide
                dataKey="y"
                name={yAxisTitle}
                domain={[zoomDomain.y[0] - 0.02, zoomDomain.y[1] + 0.02]}
                allowDataOverflow
              />
              {zAxisTitle != null && (
                <ZAxis
                  type="number"
                  dataKey="z"
                  name={zAxisTitle}
                  range={[10, 300]}
                  domain={COMPASS_DEFAULT_ZOOM_DOMAIN_WITH_BUFFER.y}
                />
              )}

              <defs>
                <linearGradient id={'full_gradient'}>
                  <stop
                    offset="0%"
                    stopColor={
                      mode === StrategyCompassMode.Compass
                        ? posColor
                        : theme.palette.compass.freeformBg
                    }
                    stopOpacity={0.45}
                  />
                  <stop
                    offset="100%"
                    stopColor={
                      mode === StrategyCompassMode.Compass
                        ? negColor
                        : theme.palette.compass.freeformBg
                    }
                    stopOpacity={0.45}
                  />
                </linearGradient>
                {visibleChartDataWithPoints.map(
                  ({ points, colorIdx, colorHSL }) =>
                    range(0, points.length).map((pointIdx) => {
                      const beginColorInterpolation = historyColorInterpolation(
                        (points.length - pointIdx) / points.length,
                        colorHSL[0],
                        colorHSL[1],
                        colorHSL[2],
                      );

                      const endColorInterpolation = historyColorInterpolation(
                        (points.length - pointIdx - 1) / points.length,
                        colorHSL[0],
                        colorHSL[1],
                        colorHSL[2],
                      );
                      return (
                        <linearGradient
                          id={`history_color_gradient_${colorIdx}_endpoint_${pointIdx}`}
                          key={colorIdx * compassHistoryLookback + pointIdx}
                        >
                          <stop
                            offset="0%"
                            stopColor={beginColorInterpolation.hsla}
                          ></stop>
                          <stop
                            offset="100%"
                            stopColor={endColorInterpolation.hsla}
                          ></stop>
                        </linearGradient>
                      );
                    }),
                )}
              </defs>

              <ReferenceArea
                x1={X_MIN}
                y1={Y_MIN}
                x2={X_MAX}
                y2={Y_MAX}
                fill={`url(#full_gradient)`}
                stroke={'rgba(0,0,0,0)'}
                fillOpacity={1}
                strokeWidth={0}
                strokeOpacity={0}
                ifOverflow="hidden"
              />

              {labeledReferenceAreas}

              {axisLinesComponent}

              {renderSelectionBox()}

              {visibleChartDataWithPoints.map(
                ({ symIdx, points, colorIdx, color, colorHSL }) =>
                  (points.length > 1 ? pairWise(points) : [points]).map(
                    (
                      lineSegmentPoints:
                        | CompassChartData[]
                        | [CompassChartData, CompassChartData],
                      pointIdx,
                    ) => {
                      const lineColor = `url(#history_color_gradient_${colorIdx}_endpoint_${pointIdx})`;

                      const dotColor =
                        compassHistoryLookback === 0
                          ? color
                          : historyColorInterpolation(
                              (points.length - pointIdx) / points.length,
                              colorHSL[0],
                              colorHSL[1],
                              colorHSL[2],
                            ).hsla;

                      return (
                        <Scatter
                          key={symIdx + (1 + pointIdx) * MAX_COMPASS_SYMS}
                          data={lineSegmentPoints}
                          dataKey="y"
                          line={{ stroke: lineColor, strokeWidth: 3 }}
                          strokeWidth={compassHistoryLookback > 0 ? 0.2 : 2}
                          stroke={dotColor}
                          fill={dotColor}
                          isAnimationActive={false}
                          onMouseEnter={() => setActiveTooltip(true)}
                          onMouseLeave={() => setActiveTooltip(false)}
                        >
                          {pointIdx === 0 && (
                            <LabelList
                              dataKey="sym"
                              content={(props: any) => {
                                if (!props?.value || props?.index !== 0) {
                                  return undefined;
                                }
                                let x = props.x - 1.75 * props.value.length;
                                let y = props.y - 3;
                                const forceRight =
                                  props.x <= EDGE_ALLOWED_SPACING;
                                const forceLeft =
                                  props.x >=
                                  chartSize.width - EDGE_ALLOWED_SPACING;

                                if (forceLeft || forceRight) {
                                  // if point is at the extreme left/right, position label next to point
                                  y += 10;
                                  x += forceRight
                                    ? 17
                                    : -5.5 * props.value.length;
                                }
                                if (props.y <= EDGE_ALLOWED_SPACING) {
                                  // if point is at the top, position label below point
                                  y += 20;
                                }

                                return (
                                  <text
                                    x={x}
                                    y={y - 10}
                                    fill={
                                      theme.colorMode === ColorMode.LIGHT
                                        ? '#000'
                                        : '#fff'
                                    }
                                    fontSize={16}
                                    fontWeight={
                                      props.value === hoveredSymbol
                                        ? 'bold'
                                        : 'normal'
                                    }
                                  >
                                    {props.value}
                                  </text>
                                );
                              }}
                            />
                          )}
                        </Scatter>
                      );
                    },
                  ),
              )}

              {/*create a special circle around the latest point*/}
              <Scatter
                data={visibleChartDataWithPoints
                  .filter(
                    ({ points }) =>
                      compassHistoryLookback > 0 && points.length >= 1,
                  )
                  .map(({ points }) => points[0])}
                dataKey="y"
                strokeWidth={1}
                stroke={
                  theme.colorMode === ColorMode.LIGHT ? '#000000' : '#ffca28aa'
                }
                fill={'#00000000'}
                isAnimationActive={false}
                onMouseEnter={() => setActiveTooltip(true)}
                onMouseLeave={() => setActiveTooltip(false)}
              />

              <RechartsTooltip
                isAnimationActive={false}
                active={tooltipStable && activeTooltip}
                content={
                  <StrategyCompassTooltip
                    chartData={chartData}
                    symDataMap={data}
                    compassParams={compassParams}
                  />
                }
              />
            </ScatterChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
